/**
 * Footer Component
 *
 * Site footer with Prismic-controlled content.
 * Includes tagline, contact email, menu links, and social links.
 */

import React from "react";
import { graphql, Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import FancyLink from "../FancyLink";
import FooterSocialLink from "../FooterSocialLink";
import * as styles from "./Footer.module.scss";

const Footer = ({ footerData }) => {
  const { tagline, contact_email, menu_links, social_links, logo, logo_link, logo_alt_text } =
    footerData || {};

  const currentYear = new Date().getFullYear();

  const menuLinksRendered = menu_links?.map((menuLink, index) => (
    <li key={`footer-link-${index}`}>
      <FancyLink href={menuLink.link?.url || "/"} target={menuLink.link?.target}>
        {menuLink.label}
      </FancyLink>
    </li>
  ));

  const socialLinksRendered = social_links?.map((socialLink, index) => (
    <FooterSocialLink
      key={`social-link-${index}`}
      href={socialLink.link?.url}
      platform={socialLink.platform}
      ariaLabel={`Follow us on ${socialLink.platform}`}
    />
  ));

  return (
    <footer className={styles.footer} data-color-scheme="black">
      <div className={`page-width--slim ${styles.container}`}>
        {/* Column 1: Tagline, contact, copyright */}
        <div className={styles.col1}>
          {tagline && <p className="h3">{tagline}</p>}
          {contact_email && (
            <FancyLink href={`mailto:${contact_email}`} className={styles.email}>
              {contact_email}
            </FancyLink>
          )}
          <small className={styles.copyright}>
            Copyright © {currentYear} Low Battery. All rights reserved.
            {/* <small>
            Website by{" "}
            <FancyLink href="https://makememodern.com" target="_blank">
              Make Me Modern
            </FancyLink>.
            </small> */}
          </small>
        </div>

        {/* Column 2: Links */}
        {menuLinksRendered && menuLinksRendered.length > 0 && (
          <nav className={styles.footerNav} aria-label="Footer navigation">
            <ul>{menuLinksRendered}</ul>
          </nav>
        )}

        {/* Column 3: Social icons */}
        {socialLinksRendered && socialLinksRendered.length > 0 && (
          <ul className={styles.socialLinks}>{socialLinksRendered}</ul>
        )}

        {/* Column 4: Logo */}
        <div className={styles.logo}>
          <Link to={logo_link?.url || "/"} aria-label={logo_alt_text || "Home"}>
            {logo?.gatsbyImageData ? (
              <GatsbyImage
                image={logo.gatsbyImageData}
                alt={logo_alt_text || ""}
                className={styles.logoImage}
              />
            ) : logo?.url ? (
              <img
                src={logo.url}
                alt={logo_alt_text || ""}
                className={styles.logoImage}
              />
            ) : (
              <span className={styles.logoText}>Low Battery</span>
            )}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

export const query = graphql`
  fragment FooterFragment on PrismicFooter {
    _previewable
    data {
      logo {
        url
        gatsbyImageData(width: 400, placeholder: NONE, layout: CONSTRAINED)
      }
      logo_link {
        url
      }
      logo_alt_text
      tagline
      contact_email
      menu_links {
        label
        link {
          url
          target
        }
      }
      social_links {
        platform
        link {
          url
        }
      }
      copyright_text
    }
  }
`;
